/*
 * Curated list of IANA timezone identifiers for the continental US and its
 * major territories. The backend stores the raw IANA string (zoneinfo is an
 * industry-standard registry), so this list is purely a UI affordance — any
 * valid IANA value the user types/picks will validate and round-trip.
 */

export const US_TIMEZONE_OPTIONS = Object.freeze([
    { value: 'Pacific/Honolulu', labelKey: 'timezones.us.honolulu' },
    { value: 'America/Anchorage', labelKey: 'timezones.us.anchorage' },
    { value: 'America/Los_Angeles', labelKey: 'timezones.us.losAngeles' },
    { value: 'America/Phoenix', labelKey: 'timezones.us.phoenix' },
    { value: 'America/Denver', labelKey: 'timezones.us.denver' },
    { value: 'America/Chicago', labelKey: 'timezones.us.chicago' },
    { value: 'America/New_York', labelKey: 'timezones.us.newYork' },
]);

const US_TIMEZONE_VALUES = new Set(US_TIMEZONE_OPTIONS.map((option) => option.value));

// Older browsers/webviews may lack Intl.supportedValuesOf('timeZone'); keep a
// broad cross-region fallback list so event creation never collapses back to a
// US-only picker in those clients.
const FALLBACK_GLOBAL_TIMEZONE_VALUES = Object.freeze([
    'UTC',
    'Africa/Cairo',
    'Africa/Casablanca',
    'Africa/Johannesburg',
    'Africa/Lagos',
    'Africa/Nairobi',
    'America/Argentina/Buenos_Aires',
    'America/Bogota',
    'America/Halifax',
    'America/Mexico_City',
    'America/Sao_Paulo',
    'America/St_Johns',
    'America/Santiago',
    'America/Toronto',
    'Asia/Bangkok',
    'Asia/Dhaka',
    'Asia/Dubai',
    'Asia/Hong_Kong',
    'Asia/Jakarta',
    'Asia/Jerusalem',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Manila',
    'Asia/Seoul',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Taipei',
    'Asia/Tokyo',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/Athens',
    'Europe/Berlin',
    'Europe/Bucharest',
    'Europe/Dublin',
    'Europe/Helsinki',
    'Europe/Istanbul',
    'Europe/Lisbon',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Rome',
    'Europe/Warsaw',
    'Pacific/Auckland',
    'Pacific/Chatham',
]);

export const DEFAULT_US_TIMEZONE = 'America/New_York';

function buildTranslatedUsTimezoneOptions(translate) {
    return US_TIMEZONE_OPTIONS.map((option) => ({
        value: option.value,
        label: translate(option.labelKey),
    }));
}

function resolveSupportedTimezones() {
    try {
        if (typeof Intl?.supportedValuesOf === 'function') {
            const supported = Intl.supportedValuesOf('timeZone');
            if (Array.isArray(supported) && supported.length > 0) {
                return supported.filter((value) => typeof value === 'string' && value.trim().length > 0);
            }
        }
    } catch (_error) {
        // Fall through to curated fallback.
    }

    return FALLBACK_GLOBAL_TIMEZONE_VALUES;
}

export function resolveViewerTimezone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (typeof tz === 'string' && tz.trim().length > 0) {
            return tz;
        }
    } catch (_error) {
        // Fall through to default — some test/SSR environments lack Intl.
    }
    return DEFAULT_US_TIMEZONE;
}

export function isUsTimezone(tz) {
    return typeof tz === 'string' && US_TIMEZONE_VALUES.has(tz);
}

/**
 * Returns the set of dropdown options to render. If the given timezone isn't
 * one of the curated US entries (e.g. the viewer is abroad, or picked an
 * override), we prepend it as an "auto-detected" option so the <select>
 * element can reflect it as the current value without falling out of sync.
 */
export function buildTimezoneOptionsForValue(currentValue, translate) {
    const base = buildTranslatedUsTimezoneOptions(translate);
    const allSupported = resolveSupportedTimezones();
    const seen = new Set(base.map((option) => option.value));
    const extraSupported = allSupported
        .filter((value) => !seen.has(value))
        .sort((left, right) => left.localeCompare(right))
        .map((value) => ({
            value,
            label: value,
        }));

    const options = [...base, ...extraSupported];

    if (!currentValue || seen.has(currentValue) || extraSupported.some((option) => option.value === currentValue)) {
        return options;
    }

    return [
        {
            value: currentValue,
            label: translate('timezones.detected', { tz: currentValue }),
        },
        ...options,
    ];
}
